/**
 * cityGrouper.js — Groups stations by city (Radio Garden style)
 *
 * Each point on the globe = a CITY, not a station.
 * Multiple stations can exist in the same city.
 * Next/Prev buttons cycle through stations within a city.
 *
 * Normalizes city names to merge variants like:
 *   "São Paulo (SP)" → "São Paulo"
 *   "Rio de Janeiro (RJ)" → "Rio de Janeiro"
 */

/**
 * Normalize city name by removing state/region suffixes.
 * "São Paulo (SP)" → "São Paulo"
 * "Belo Horizonte - MG" → "Belo Horizonte"
 */
function normalizeCity(city) {
  if (!city) return 'Unknown';

  return city
    // Remove parenthetical suffixes: (SP), (RJ), (MG), etc.
    .replace(/\s*\([^)]{1,10}\)\s*$/, '')
    // Remove dash suffixes: " - SP", " - RJ"
    .replace(/\s*[-–]\s*[A-Z]{2}\s*$/, '')
    // Remove trailing slash suffixes: " / SP"
    .replace(/\s*\/\s*[A-Z]{2}\s*$/, '')
    .trim();
}

/**
 * Groups flat station array into city-based points.
 * Stations with the same normalized `city + country_code` are grouped together.
 *
 * @param {Array} stations - Flat array of station objects
 * @returns {{ cityPoints: Array, cityMap: Map }}
 */
export function groupStationsByCity(stations) {
  const groups = new Map();

  for (const station of stations) {
    // Normalize city name before grouping
    const city = normalizeCity(station.city);
    const key = `${city}|${station.country_code}`;

    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        city,
        state: station.state || null,
        country_code: station.country_code,
        country_name: station.country_name,
        stations: [],
        // Will be calculated as average
        latSum: 0,
        lngSum: 0,
      });
    }

    const group = groups.get(key);
    group.stations.push(station);
    group.latSum += station.latitude;
    group.lngSum += station.longitude;
  }

  // Build final cityPoints array
  const cityPoints = [];
  const cityMap = new Map();

  for (const [key, group] of groups) {
    const count = group.stations.length;
    const cityPoint = {
      id: key,
      city: group.city,
      state: group.state,
      country_code: group.country_code,
      country_name: group.country_name,
      latitude: group.latSum / count,
      longitude: group.lngSum / count,
      stationCount: count,
      stations: group.stations,
    };

    cityPoints.push(cityPoint);
    cityMap.set(key, cityPoint);
  }

  return { cityPoints, cityMap };
}

/**
 * Get point radius based on station count.
 * More stations → bigger point (logarithmic scale).
 * Kept small like Radio Garden for clean dense areas.
 */
export function getCityPointRadius(stationCount) {
  if (stationCount <= 1) return 0.18;
  return 0.18 + Math.log2(stationCount) * 0.08;
}
